import { Component, DoCheck, Inject, Input } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { ReportSeriesModel } from '../../../Common/Reports/models/report-series.model';
import { CardChartComponentHighlightColorEnum, CardChartComponentLayoutEnum } from '../../../Common/Charts/components/CardChart/card-chart.component';
import { DataPointModel } from '../../../Common/Charts/models/data-point.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';

import { AnswerEngineOption } from '../../models/answer-engine-option.model';
import { AnswerEngineTypesEnum } from '../../enums/answer-engine-types.enum';
import { ViMetadata } from '../../../api/mentions/model/vi-metadata.api.model';

const NOT_AVAILABLE = 'N/A';
interface CitedDomainLink {
    href: string;
    label: string;
}
interface CardMetric {
    series: ReportSeriesModel[];
    urls?: CitedDomainLink[];
    customValue?: string; // For non-numeric text (e.g. "N/A", "True"/"False");
    tooltip?: string;
}

@Component({
    selector: 'agpr-vi-answer-scores',
    templateUrl: './vi-answer-scores.component.html',
    styleUrls: ['./vi-answer-scores.component.scss'],
    standalone: false
})
export class ViAnswerScoresComponent implements DoCheck {
    @Input() id: string = 'viAnswerScores';
    @Input() engines: AnswerEngineOption[] = [];
    /** Same question's answers from the chronologically-previous date, for the comparison bubble on a handful of metrics */
    @Input() previousEngines: AnswerEngineOption[] = [];
    /** The brand's own domains (`ViBrandIdentity.websites`), for matching against citedSources in the Own Domain Cited card */
    @Input() ownDomains: string[] = [];

    readonly cardChartLayout = CardChartComponentLayoutEnum;
    readonly cardChartComponentHighlightColors = CardChartComponentHighlightColorEnum;
    readonly messageSeverity = messageSeverityEnum;

    /** null selects the "All Engines" aggregate, shown as "Overall" in the header */
    selectedEngine?: AnswerEngineOption;
    isUnavailable: boolean = false;
    brandScore: CardMetric = this.emptyMetric();
    brandMentioned: CardMetric = this.emptyMetric();
    mentionRank: CardMetric = this.emptyMetric();
    organicInclusion: CardMetric = this.emptyMetric();
    sentiment: CardMetric = this.emptyMetric();
    accuracy: CardMetric = this.emptyMetric();
    riskFlag: CardMetric = this.emptyMetric();
    // TODO: ViMetadata has no evidence field yet; swap in real values once the backend adds it
    // evidenceSeries: ReportSeriesModel[] = [];
    citationCount: CardMetric = this.emptyMetric();
    ownDomainCited: CardMetric = this.emptyMetric();
    competitorsMentioned: CardMetric = this.emptyMetric();

    private cachedEngineStates: { rawResponse: unknown; loading: boolean }[] = [];

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private localizationService: LocalizationService,
    ) {}

    ngDoCheck(): void {
        const engineStates = [...this.engines, ...this.previousEngines]
            .map(engine => ({ rawResponse: engine.rawResponse, loading: !!engine.loading }));
        const changed = engineStates.length !== this.cachedEngineStates.length
            || engineStates.some((state, index) => {
                const cached = this.cachedEngineStates[index];
                return !cached || state.rawResponse !== cached.rawResponse || state.loading !== cached.loading;
            });

        if (changed) {
            this.cachedEngineStates = engineStates;
            this.refresh();
        }
    }

    onEngineSelectionChange(engineType: AnswerEngineTypesEnum | null): void {
        this.selectedEngine = this.engines.find(engine => engine.type === engineType);
        this.refresh();
    }

    private refresh(): void {
        const selectedType = this.selectedEngine?.type;
        if (selectedType) {
            this.selectedEngine = this.engines.find(engine => engine.type === selectedType) ?? this.selectedEngine;
        }

        const metadatas = this.getMetadatas(this.engines);
        const hasData = metadatas.length > 0;
        const isLoading = this.selectedEngine ? !!this.selectedEngine.loading : this.engines.some(engine => engine.loading);
        this.isUnavailable = !hasData && !isLoading;

        const previousMetadatas = this.getMetadatas(this.previousEngines);
        const previousHasData = previousMetadatas.length > 0;

        this.brandScore = this.buildNumericMetric(
            'Vi.qnA.answerScore.brandScore',
            hasData,
            isLoading,
            this.averageOf(metadatas.map(metadata => metadata.brandScore)),
            undefined,
            { hasData: previousHasData, value: this.averageOf(previousMetadatas.map(metadata => metadata.brandScore)) }
        );

        this.brandMentioned = this.selectedEngine
            ? this.buildBooleanMetric(
                'Vi.qnA.answerScore.brandMentioned',
                hasData,
                isLoading,
                metadatas[0]?.brandMentioned
            )
            : this.buildNumericMetric(
                'Vi.qnA.answerScore.brandMentioned',
                hasData,
                isLoading,
                this.rateOf(metadatas.map(metadata => metadata.brandMentioned))
            );

        this.mentionRank = this.buildNumericMetric(
            'Vi.qnA.answerScore.mentionRank',
            hasData,
            isLoading,
            this.averageOf(metadatas.map(metadata => metadata.mentionRank))
        );

        this.organicInclusion = this.buildNumericMetric(
            'Vi.qnA.answerScore.organicInclusion',
            hasData,
            isLoading,
            this.averageOf(metadatas.map(metadata => metadata.organicInclusion)),
            undefined,
            { hasData: previousHasData, value: this.averageOf(previousMetadatas.map(metadata => metadata.organicInclusion)) }
        );

        const sentimentScore = this.averageOf(metadatas.map(metadata => this.getSentimentScore(metadata.sentiment)));
        const previousSentimentScore = this.averageOf(previousMetadatas.map(metadata => this.getSentimentScore(metadata.sentiment)));
        this.sentiment = this.buildNumericMetric(
            'Vi.qnA.answerScore.sentiment',
            hasData,
            isLoading,
            sentimentScore,
            this.getSentimentText(sentimentScore),
            { hasData: previousHasData, value: previousSentimentScore }
        );

        this.accuracy = this.buildNumericMetric(
            'Vi.qnA.answerScore.accuracy',
            hasData,
            isLoading,
            this.averageOf(metadatas.map(metadata => metadata.factualAccuracy)),
            undefined,
            { hasData: previousHasData, value: this.averageOf(previousMetadatas.map(metadata => metadata.factualAccuracy)) }
        );

        const flaggedMetadatas = metadatas.filter(metadata => metadata.riskFlag);
        this.riskFlag = this.selectedEngine
            ? this.buildBooleanMetric(
                'Vi.qnA.answerScore.riskFlag',
                hasData,
                isLoading,
                metadatas[0]?.riskFlag,
                metadatas[0]?.riskNote
            )
            : {
                series: [this.buildSeries('Vi.qnA.answerScore.riskFlag', hasData, isLoading, this.rateOf(metadatas.map(metadata => metadata.riskFlag)))],
                customValue: hasData
                    ? (flaggedMetadatas.length > 0
                        ? this.localizationService.transform(
                            'Vi.qnA.answerScore.riskFlag.flaggedOfTotal',
                            { flagged: flaggedMetadatas.length, total: metadatas.length }
                        )
                        : this.localizationService.transform('Vi.qnA.answerScore.none'))
                    : (isLoading ? undefined : NOT_AVAILABLE)
            };

        // TODO: no "evidence" field exists on ViMetadata yet
        // this.evidenceSeries = this.buildNumericMetric(
        //     'Vi.qnA.answerScore.evidence',
        //     hasData,
        //     isLoading,
        //     this.averageOf(metadatas.map(metadata => metadata.evidence)),
        //     undefined,
        //     { hasData: previousHasData, value: this.averageOf(previousMetadatas.map(metadata => metadata.evidence)) }
        // );

        this.citationCount = this.buildNumericMetric(
            'Vi.qnA.answerScore.citationCount',
            hasData,
            isLoading,
            this.averageOf(metadatas.map(metadata => metadata.citationCount)),
            undefined,
            { hasData: previousHasData, value: this.averageOf(previousMetadatas.map(metadata => metadata.citationCount)) }
        );

        this.ownDomainCited = this.selectedEngine
            ? this.buildBooleanMetric(
                'Vi.qnA.answerScore.ownDomainCited',
                hasData,
                isLoading,
                metadatas[0]?.ownDomainCited
            )
            : this.buildNumericMetric(
                'Vi.qnA.answerScore.ownDomainCited',
                hasData,
                isLoading,
                this.rateOf(metadatas.map(metadata => !!metadata.ownDomainCited))
            );
        const citedDomains = new Set((metadatas[0]?.citedSources ?? []).map(url => this.getDomain(url)));
        this.ownDomainCited.urls = this.selectedEngine
            ? this.ownDomains
                .filter(domain => citedDomains.has(this.getDomain(domain)))
                .map(domain => ({ href: domain, label: this.getDomain(domain) }))
            : [];
        this.competitorsMentioned = this.selectedEngine
            ? this.buildBooleanMetric(
                'Vi.qnA.answerScore.competitorsMentioned',
                hasData,
                isLoading,
                metadatas[0]?.competitorBenchmarks?.some(benchmark => !!benchmark.mentioned)
            )
            : this.buildNumericMetric(
                'Vi.qnA.answerScore.competitorsMentioned',
                hasData,
                isLoading,
                this.rateOf(
                    metadatas.map(
                        metadata => (metadata.competitorBenchmarks ?? []).some(benchmark => !!benchmark.mentioned)
                    )
                )
            );
    }

    private getMetadatas(engines: AnswerEngineOption[]): ViMetadata[] {
        const selectedType = this.selectedEngine?.type;
        const selectedEngines = selectedType
            ? engines.filter(engine => engine.type === selectedType)
            : engines;
        return selectedEngines
            .map(engine => engine.rawResponse as ViMetadata | undefined)
            .filter((x): x is ViMetadata => !!x);
    }

    private getSentimentScore(sentiment: string | undefined | null): number | undefined {
        switch (sentiment?.toLowerCase()) {
            case 'negative': return 0;
            case 'neutral': return 50;
            case 'positive': return 100;
            default: return undefined;
        }
    }

    private getSentimentText(score: number | null): string {
        if (score === null) {
            return '';
        } else if (score >= 0 && score < 25) {
            return 'Vi.qnA.answerScore.sentiment.negative';
        } else if (score >= 25 && score <= 75) {
            return 'Vi.qnA.answerScore.sentiment.neutral';
        } else if (score > 75 && score <= 100) {
            return 'Vi.qnA.answerScore.sentiment.positive';
        } else {
            return '';
        }
    }

    private averageOf(values: (number | null | undefined)[]): number | null {
        const numbers = values.filter((value): value is number => value !== null && value !== undefined && !isNaN(value));
        return numbers.length ? numbers.reduce((total, value) => total + value, 0) / numbers.length : null;
    }

    private rateOf(flags: (boolean | null | undefined)[]): number | null {
        return flags.length ? (flags.filter(x => !!x).length / flags.length) * 100 : null;
    }

    private getDomain(url: string): string {
        try {
            return new URL(url).hostname.replace(/^www\./i, '').toLowerCase();
        } catch {
            return url.replace(/^www\./i, '').toLowerCase();
        }
    }

    private buildNumericMetric(
        label: string,
        hasData: boolean,
        isLoading: boolean,
        value: number | null | undefined,
        tooltip?: string,
        previous?: { hasData: boolean; value: number | null | undefined }
    ): CardMetric {
        const isValidValue = hasData && value !== null && value !== undefined && !isNaN(value);
        return {
            series: previous
                ? [
                    this.buildSeries(label, isValidValue, isLoading, value),
                    this.buildSeries(label, previous.hasData, false, previous.value)
                ]
                : [this.buildSeries(label, isValidValue, isLoading, value)],
            customValue: isValidValue || isLoading ? undefined : NOT_AVAILABLE,
            ... (tooltip ? { tooltip } : {})
        };
    }

    private buildBooleanMetric(label: string, hasData: boolean, isLoading: boolean, value: boolean | null | undefined, tooltip?: string): CardMetric {
        const isValidValue = hasData && value !== null && value !== undefined;
        return {
            series: [this.buildSeries(label, isValidValue, isLoading, value === true ? 1 : value === false ? 0 : null)],
            customValue: isValidValue
                ? this.localizationService.transform(value ? 'Vi.qnA.answerScore.true' : 'Vi.qnA.answerScore.false')
                : (isLoading ? undefined : NOT_AVAILABLE),
            ... (tooltip ? { tooltip } : {})
        };
    }

    private emptyMetric(): CardMetric {
        return { series: [], customValue: NOT_AVAILABLE };
    }

    private buildSeries(label: string, hasData: boolean, isLoading: boolean, value: number | null | undefined): ReportSeriesModel {
        const series = new ReportSeriesModel();
        series.label = label;
        const data = hasData ? [{ count: value ?? 0 }] : (isLoading ? null : []);
        series.dataProvider.next(data as DataPointModel[]);
        return series;
    }
}
